// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Info, ShieldCheck, X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { Logo } from '@/components/Logo'
import { cn } from '@/lib/utils'

interface ConsentFormProps {
  checked: Record<string, boolean>
  onToggle: (id: string) => void
  onContinue: () => void
  onDecline: () => void
}

function ConsentForm({ checked, onToggle, onContinue, onDecline }: ConsentFormProps) {
  const { t } = useTranslation()

  const CONSENT_ITEMS = [
    { id: 'hipaa',  title: t('consent.item_hipaa_title'),  description: t('consent.item_hipaa_desc') },
    { id: 'health', title: t('consent.item_health_title'), description: t('consent.item_health_desc') },
    { id: 'ai',     title: t('consent.item_ai_title'),     description: t('consent.item_ai_desc') },
    { id: 'comms',  title: t('consent.item_comms_title'),  description: t('consent.item_comms_desc') },
  ]

  const allChecked = CONSENT_ITEMS.every((item) => checked[item.id])
  return (
    <>
      <div className="flex flex-col gap-3 mb-4">
        {CONSENT_ITEMS.map((item) => (
          <label
            key={item.id}
            className={cn(
              'flex items-start gap-4 bg-card rounded-2xl px-4 py-4 border cursor-pointer transition-all duration-200',
              checked[item.id]
                ? 'border-primary/40 bg-primary/4 shadow-sm'
                : 'border-border hover:border-primary/30'
            )}
          >
            <Checkbox
              checked={!!checked[item.id]}
              onCheckedChange={() => onToggle(item.id)}
              className="mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="font-semibold text-foreground text-[14px] leading-snug mb-0.5">
                {item.title}
              </p>
              <p className="text-muted-foreground text-[13px] leading-relaxed">
                {item.description}
              </p>
            </div>
          </label>
        ))}
      </div>
      <p className="text-[12px] text-muted-foreground text-center mb-4 flex items-center justify-center gap-1.5">
        <Info className="w-3.5 h-3.5 text-[#02C7C3] flex-shrink-0" />
        {t('consent.allRequired')}
      </p>
      <button
        disabled={!allChecked}
        onClick={onContinue}
        className={cn(
          'w-full h-11 rounded-xl font-semibold text-sm text-white flex items-center justify-center bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-lg shadow-primary/20 transition-opacity',
          !allChecked ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90 active:opacity-80'
        )}
      >
        {t('consent.continueButton')}
      </button>
      <button
        type="button"
        onClick={onDecline}
        className="mt-3 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
      >
        {t('consent.declineButton')}
      </button>
    </>
  )
}

function DeclinedView() {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center text-center px-4 py-8 animate-in fade-in duration-200">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4">
        <X className="w-7 h-7 text-muted-foreground" />
      </div>
      <h2 className="text-xl font-bold text-foreground mb-2">{t('consent.declinedTitle')}</h2>
      <p className="text-muted-foreground text-[14px] leading-relaxed mb-6 max-w-xs">
        {t('consent.declinedBody')}
      </p>
      <p className="text-[12px] text-muted-foreground">{t('consent.declinedNote')}</p>
    </div>
  )
}

export function ConsentScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [declined, setDeclined] = useState(false)
  const toggle = (id: string) => setChecked((prev) => ({ ...prev, [id]: !prev[id] }))

  return (
    <OnboardingLayout>
      {/* Mobile */}
      <div className="flex flex-col flex-1 md:hidden">
        <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9] flex-shrink-0" />
        <div className="flex-1 overflow-y-auto px-5 pt-10 pb-6">
          {!declined && (
            <>
              <div className="flex items-center justify-center gap-2 mb-1">
                <ShieldCheck className="w-5 h-5 text-[#02C7C3]" />
                <h1 className="text-2xl font-bold text-foreground text-center">
                  {t('consent.headline')}
                </h1>
              </div>
              <p className="text-muted-foreground text-[14px] text-center mb-8">
                {t('consent.subheadline')}
              </p>
            </>
          )}
          {declined
            ? <DeclinedView />
            : <ConsentForm checked={checked} onToggle={toggle} onContinue={() => navigate('/chat')} onDecline={() => setDeclined(true)} />
          }
        </div>
      </div>

      {/* Desktop */}
      <div className="hidden md:flex flex-col flex-1">
        <header className="px-10 py-5 flex items-center gap-3 border-b border-border bg-card">
          <Logo variant="wordmark" className="h-8 w-auto" />
        </header>
        <div className="flex flex-1 items-start justify-center px-6 py-12 overflow-y-auto">
          <div className="w-full max-w-lg">
            <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9] rounded-full mb-8" />
            {!declined && (
              <>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <ShieldCheck className="w-5 h-5 text-[#02C7C3]" />
                  <h1 className="text-3xl font-bold text-foreground text-center">
                    {t('consent.headline')}
                  </h1>
                </div>
                <p className="text-muted-foreground text-center mb-8">
                  {t('consent.subheadline')}
                </p>
              </>
            )}
            {declined
              ? <DeclinedView />
              : <ConsentForm checked={checked} onToggle={toggle} onContinue={() => navigate('/chat')} onDecline={() => setDeclined(true)} />
            }
          </div>
        </div>
      </div>
    </OnboardingLayout>
  )
}
